import { useState, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { Search, Loader2, X } from 'lucide-react';
import { searchGraphUsers, listUsersByLocation } from '../api/graph.js';
import type { GraphEmployee } from '../api/graph.js';
import { graphLocationRequest } from '../config/msal-config.js';

interface EmployeePickerProps {
  value: GraphEmployee | null;
  onSelect: (employee: GraphEmployee | null) => void;
  disabled?: boolean;
  /**
   * Entra companyName for the selected rooftop. When provided, the picker
   * pre-fetches all employees at that location and shows them on focus.
   * Graph search (User.ReadBasic.All) takes over once query length >= 2.
   */
  location?: string;
}

export function EmployeePicker({
  value,
  onSelect,
  disabled,
  location,
}: EmployeePickerProps): React.JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GraphEmployee[]>([]);
  const [locationEmployees, setLocationEmployees] = useState<GraphEmployee[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [locationError, setLocationError] = useState<string | null>(null);
  // Diagnostic escape-hatch: when a user can't find who they're looking for
  // in the location-scoped roster (typically because the target's Entra
  // companyName isn't in LOCATION_ALIASES yet), they can click "Search all
  // rooftops" to fall back to tenant-wide $search. Results display the raw
  // Entra companyName so an Executive can discover the missing alias
  // without needing Graph Explorer access. Resets on select/clear.
  const [tenantWideOverride, setTenantWideOverride] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { instance } = useMsal();

  // Fetch location roster when the rooftop changes.
  // Silent-only by design: avoids popup races with the search effect, which
  // uses a different Graph scope and its own popup fallback. If the silent
  // token acquire or the Graph call fails, the error is surfaced in the
  // dropdown rather than swallowed — the picker is location-scoped once a
  // rooftop is chosen, so a silent empty result would hide all employees.
  useEffect(() => {
    if (location === undefined || location === '') {
      setLocationEmployees([]);
      setLocationError(null);
      return;
    }
    let cancelled = false;
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (account === undefined) return;
    setLocationLoading(true);
    setLocationError(null);
    void instance
      .acquireTokenSilent({ ...graphLocationRequest, account })
      .then(async () => listUsersByLocation(instance, location))
      .then((employees) => {
        if (!cancelled)
          setLocationEmployees(
            [...employees].sort((a, b) => a.displayName.localeCompare(b.displayName)),
          );
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLocationError(err instanceof Error ? err.message : 'Failed to load roster');
          setLocationEmployees([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLocationLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [location, instance]);

  // When a rooftop is selected we commit to location-scoped mode so users
  // never see employees from other locations — even if the location roster
  // is empty or still loading. Tenant-wide Graph search is only allowed
  // when no location is provided OR the Executive explicitly opts in via
  // the "Search all rooftops" escape-hatch.
  const locationScoped = location !== undefined && location !== '';
  const effectiveScoped = locationScoped && !tenantWideOverride;
  const filteredLocation =
    query === ''
      ? locationEmployees
      : locationEmployees.filter((e) => e.displayName.toLowerCase().includes(query.toLowerCase()));
  const displayList = effectiveScoped ? filteredLocation : results;

  const isOpen =
    isFocused &&
    (locationLoading ||
      (effectiveScoped
        ? true // when scoped to a rooftop, always show feedback (roster, empty, or error)
        : query.length >= 2 && (isLoading || results.length > 0 || error !== null)));

  // Debounced Graph search — runs in tenant-wide mode (no location scoped
  // or escape-hatch engaged) whenever query >= 2.
  useEffect(() => {
    if (query.length < 2 || effectiveScoped) {
      setResults([]);
      return;
    }

    let cancelled = false;
    const timerId = setTimeout(() => {
      void (async () => {
        setIsLoading(true);
        setError(null);
        try {
          const employees = await searchGraphUsers(instance, query);
          if (!cancelled)
            setResults([...employees].sort((a, b) => a.displayName.localeCompare(b.displayName)));
        } catch (err) {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Search failed');
            setResults([]);
          }
        } finally {
          if (!cancelled) setIsLoading(false);
        }
      })();
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timerId);
    };
  }, [query, instance, effectiveScoped]);

  // Close dropdown on click outside
  useEffect(() => {
    function handleClickOutside(e: MouseEvent): void {
      if (containerRef.current !== null && !containerRef.current.contains(e.target as Node)) {
        setIsFocused(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleSelect(employee: GraphEmployee): void {
    onSelect(employee);
    setQuery('');
    setResults([]);
    setIsFocused(false);
    setTenantWideOverride(false);
  }

  function handleClear(): void {
    onSelect(null);
    setQuery('');
    setResults([]);
    setTenantWideOverride(false);
    inputRef.current?.focus();
  }

  // Mode B — employee selected: show chip with clear button
  if (value !== null) {
    return (
      <div className="flex items-center gap-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white">
        <span className="flex-1 font-medium text-gray-900">{value.displayName}</span>
        <span className="text-xs text-gray-500 truncate max-w-[160px]">
          {value.mail ?? value.userPrincipalName}
        </span>
        <button
          type="button"
          onClick={handleClear}
          disabled={disabled}
          className="ml-1 rounded p-0.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 disabled:pointer-events-none"
          aria-label="Clear selection"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  // Mode A — no selection: search input + results dropdown
  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          placeholder={
            location !== undefined && location !== ''
              ? 'Select or type to search...'
              : 'Type a name to search...'
          }
          className="w-full rounded-lg border border-gray-300 pl-8 pr-8 py-2 text-sm focus:border-brand focus:ring-1 focus:ring-brand focus:outline-none disabled:bg-gray-100 disabled:text-gray-500"
        />
        {(isLoading || locationLoading) && (
          <Loader2 className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
        )}
      </div>

      {isOpen && (
        <ul className="absolute z-20 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg max-h-60 overflow-auto py-1">
          {effectiveScoped && !locationLoading && filteredLocation.length > 0 && (
            <li className="px-3 py-1 text-xs font-medium text-gray-400 uppercase tracking-wide">
              Employees at this location
            </li>
          )}
          {tenantWideOverride && !isLoading && (
            <li className="flex items-center justify-between px-3 py-1 text-xs font-medium text-amber-800 bg-amber-50 uppercase tracking-wide">
              <span>Searching all rooftops</span>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setTenantWideOverride(false);
                }}
                className="text-xs font-normal normal-case text-amber-800 underline hover:text-amber-900"
              >
                back to {location}
              </button>
            </li>
          )}
          {locationError !== null ? (
            <li className="px-3 py-2 text-sm text-red-600">
              Could not load roster: {locationError}
            </li>
          ) : error !== null ? (
            <li className="px-3 py-2 text-sm text-red-600">{error}</li>
          ) : locationLoading || isLoading ? (
            <li className="px-3 py-2 text-sm text-gray-500">Loading...</li>
          ) : effectiveScoped && locationEmployees.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-500">No employees at this location</li>
          ) : displayList.length === 0 ? (
            <>
              <li className="px-3 py-2 text-sm text-gray-500">
                {effectiveScoped ? 'No match at this rooftop' : 'No matches found'}
              </li>
              {effectiveScoped && query.length >= 2 && (
                <li>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setTenantWideOverride(true);
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-brand hover:bg-gray-50"
                  >
                    Search all rooftops →
                  </button>
                </li>
              )}
            </>
          ) : (
            displayList.map((emp) => (
              <li
                key={emp.id}
                onMouseDown={(e) => {
                  // Prevent input blur before the selection registers
                  e.preventDefault();
                  handleSelect(emp);
                }}
                className="flex flex-col px-3 py-2 cursor-pointer hover:bg-gray-50 text-sm"
              >
                <span className="font-medium text-gray-900">{emp.displayName}</span>
                <span className="text-xs text-gray-500">{emp.mail ?? emp.userPrincipalName}</span>
                {emp.companyName !== undefined && emp.companyName !== '' && (
                  <span className="text-xs text-gray-400">{emp.companyName}</span>
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
